import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/modal";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CircularProgress,
  CloseButton,
  FormControl,
  FormLabel,
  Select,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect } from "react";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import { Glossary } from "src/components/Text/Glossary";
import {
  bulkGetOfferExpirationInfo,
  type BulkGetOfferExpirationInfoResponse,
} from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { FormSchoolGradeNotFoundError, OfferError } from "../errors";
import {
  useFetchGradeIdsWithFormSchool,
  useFetchSeatsAvailable,
} from "../graphql/hooks";
import { GET_PROGRAM_GROUPS_BY_ORG } from "../graphql/queries";
import { FormSchoolStatus } from "../types";

type Confirmed = { result: "Confirmed"; programId: string | null };
type Cancelled = { result: "Cancelled" };
export type ConfirmationResult = Confirmed | Cancelled;

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "You will extend an offer to the selected school for the family to either accept or decline. You can revoke the offer anytime.",
    "You will extend offers to the selected schools for families to either accept or decline. You can revoke offers anytime."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, make offer", "Yes, make offers");

export const useMakeOfferConfirmationDialog = (
  schoolForms: FormSchoolStatus[]
) => {
  const promiseRef = React.useRef<
    { resolve: (value: ConfirmationResult) => void } | undefined
  >();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [offerCount, setOfferCount] = React.useState(0);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [availablePrograms, setAvailablePrograms] = React.useState<
    GQL.GetProgramGroupsByOrg_program_group_programs[] | null
  >();
  const [programGroupName, setProgramGroupName] = React.useState("Program");
  const [programSelected, setProgramSelected] = React.useState<string | null>(
    null
  );
  const [schoolGradesOverCapacity, setSchoolGradesOverCapacity] =
    React.useState<{ school: string; grade: string }[]>();
  const [closedCapacityWarning, setClosedCapacityWarning] =
    React.useState(false);

  const [closedOfferExpiredInfo, setClosedOfferExpiredInfo] =
    React.useState(false);

  const [expiredOffersExpanded, setExpiredOffersExpanded] =
    React.useState(true);

  const [offerExpirationInfo, setOfferExpirationInfo] = React.useState<
    BulkGetOfferExpirationInfoResponse["data"]
  >([]);

  const organization = useOrganization();
  const queryClient = useQueryClient();

  const offerExpirationQuery = useQuery({
    queryKey: ["bulkGetOfferExpirationInfo", schoolForms],
    queryFn: async () => {
      const req = schoolForms.map((x) => ({
        form_id: x.form_id,
        school_id: x.school_id,
      }));
      return bulkGetOfferExpirationInfo({ body: req }).then((r) => r.data);
    },
    enabled: isOpen,
  });

  const offerExpirationRD: RD.RemoteData<
    Error | null,
    BulkGetOfferExpirationInfoResponse
  > = RD.useFromRestResultMemoized(offerExpirationQuery);

  const { remoteData: programGroupData } = useRemoteDataQuery<
    GQL.GetProgramGroupsByOrg,
    GQL.GetProgramGroupsByOrgVariables
  >(GET_PROGRAM_GROUPS_BY_ORG, {
    variables: {
      organization_id: organization.map((org) => org.id).toNullable(),
    },
    skip: !organization.hasData(),
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (programGroupData.hasData()) {
      const programs = programGroupData.data.program_group.flatMap(
        (group) => group.programs
      );
      const hasAvailablePrograms = programs && programs.length > 0;
      if (!hasAvailablePrograms) {
        setProgramSelected(null);
      }
      setAvailablePrograms(hasAvailablePrograms ? programs : null);

      // Note: We can modify this new array without affecting the cached data.
      const programGroupNames = programGroupData.data.program_group.map(
        (group) => group.name
      );
      const lastProgramGroup = programGroupNames.pop();
      if (lastProgramGroup) {
        setProgramGroupName(
          programGroupNames.length > 0
            ? programGroupNames.join(", ") + " or " + lastProgramGroup
            : lastProgramGroup
        );
      }
    }
  }, [programGroupData]);

  useEffect(() => {
    if (offerExpirationRD.hasData() && offerExpirationRD.data) {
      setOfferExpirationInfo(offerExpirationRD.data.data ?? []);
    }
  }, [offerExpirationRD]);

  const confirm = React.useCallback(
    async (
      schoolForms: FormSchoolStatus[],
      count = schoolForms.length
    ): Promise<ConfirmationResult> => {
      setOfferCount(count);
      queryClient.invalidateQueries({
        queryKey: ["bulkGetOfferExpirationInfo"],
      });
      onOpen();
      return new Promise<ConfirmationResult>((resolve, reject) => {
        promiseRef.current = { resolve };
      });
    },
    [onOpen, queryClient]
  );

  const onCancel = () => {
    onClose();
    if (promiseRef.current) promiseRef.current.resolve({ result: "Cancelled" });
    // reset state on closing the dialog
    setClosedCapacityWarning(false);
    setClosedOfferExpiredInfo(false);
    if (programSelected) {
      setSchoolGradesOverCapacity(undefined);
      setProgramSelected(null);
    }
  };

  const isMissingProgram = !!availablePrograms?.length && !programSelected;
  const onConfirm = () => {
    if (isMissingProgram) {
      // The confirm button should have been disabled, so if we get here it's
      // likely the result of a race condition; just ignore the event.
      return;
    }
    onClose();
    if (promiseRef.current)
      promiseRef.current.resolve({
        result: "Confirmed",
        programId: programSelected,
      });
  };

  const fetchGradeIdsWithFormSchool = useFetchGradeIdsWithFormSchool();
  const fetchSeatsAvailable = useFetchSeatsAvailable();
  const checkCapacitiesForProgram = React.useCallback(
    async (programId: string | null) => {
      try {
        // TODO: This function is also called when making offers in `MakeOffer.tsx`
        // and error handling is handled there rather than here.
        // We might want to just do the error handling here instead.
        const formSchoolGrades = await fetchGradeIdsWithFormSchool(
          schoolForms,
          programId
        );
        const gradeIds = _.uniq(
          formSchoolGrades.map((result) => {
            if (!result.form_id) {
              throw new Error("Form_id is null");
            }

            if (!result.school_id || !result.grade_id) {
              throw new OfferError(
                "One of the selected form(s) has not selected a school or grade"
              );
            }
            return result.grade_id;
          })
        );
        const seatsAvailableByGrade = await fetchSeatsAvailable(gradeIds);
        const gradesOverCapacity = seatsAvailableByGrade
          .filter(
            (grade) =>
              formSchoolGrades.filter((result) => result.grade_id === grade.id)
                .length > (grade.bin.seats_available ?? 0)
          )
          .map((gradeOverCapacity) => ({
            school: gradeOverCapacity.school.name,
            grade: gradeOverCapacity.grade_config.label,
          }));
        setSchoolGradesOverCapacity(gradesOverCapacity);
      } catch (err: any) {
        if (!(err instanceof FormSchoolGradeNotFoundError)) {
          console.error(err);
        }
        // TODO: handle the error better, since this will fail
        // when making offers.
        //
        // The root cause is that the we retrieve
        // all available programs for the org, but the program is not applicable
        // for the school and grade.
        setSchoolGradesOverCapacity(undefined);
      }
    },
    [fetchGradeIdsWithFormSchool, fetchSeatsAvailable, schoolForms]
  );

  useEffect(() => {
    if (programSelected !== undefined)
      checkCapacitiesForProgram(programSelected);
  }, [
    checkCapacitiesForProgram,
    closedCapacityWarning,
    programSelected,
    schoolForms,
  ]);

  const renderSchoolGradesOverCapacity = () => {
    if (!schoolGradesOverCapacity) return;
    const content = "By making offers, you will be over capacity at";
    const schoolGradeStrings = schoolGradesOverCapacity.map(
      (schoolGrade) => schoolGrade.school + ", " + schoolGrade.grade
    );
    if (schoolGradeStrings.length === 1) {
      return content + " " + schoolGradeStrings[0] + ".";
    }
    return (
      <>
        <p>{content}</p>
        {schoolGradeStrings.map((str, idx) => (
          <p key={idx}>
            {str}
            {idx < schoolGradeStrings.length - 1 ? "," : "."}
          </p>
        ))}
      </>
    );
  };

  const renderExpiredOfferInfoBody = () => {
    if (!offerExpirationInfo) return null;

    return (
      <Box width="100%">
        <Text mb={2}>
          {maybePluralize(offerCount, "Offer will", "Offers will")}{" "}
          <Text as="span" fontWeight="bold">
            expire automatically
          </Text>{" "}
          for {offerExpirationInfo.length}{" "}
          {maybePluralize(offerExpirationInfo.length, "school")}
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="black"
          leftIcon={
            expiredOffersExpanded ? <RiArrowUpSLine /> : <RiArrowDownSLine />
          }
          onClick={() => setExpiredOffersExpanded(!expiredOffersExpanded)}
          mt={4}
          mb={4}
        >
          {expiredOffersExpanded ? "Collapse all" : "Expand all"}
        </Button>
        {expiredOffersExpanded && (
          <Box as="ul" width="100%" pl={5} sx={{ listStyleType: "disc" }}>
            {offerExpirationInfo.map((item, index) => (
              <Box as="li" key={index}>
                <Text as="span" fontWeight="bold">
                  {item.schoolName},
                </Text>{" "}
                expires in {item.expirationDays}d, on {item.expirationTime}.
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const confirmationDialog = (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCancel}
      isCentered
    >
      <AlertDialogOverlay className={WEGLOT_APPLY_CLASS}>
        <AlertDialogContent marginX={4}>
          <AlertDialogHeader
            fontSize="lg"
            borderBottom="1px solid"
            borderColor="gray.300"
          >
            {`Make ${offerCount} ${maybePluralize(offerCount, "offer")}?`}
          </AlertDialogHeader>
          <AlertDialogBody
            fontSize="md"
            maxHeight="40vh"
            sx={{ overflowY: "auto" }}
          >
            <VStack gap={2}>
              <Text>
                <Glossary>{confirmationBodyText(offerCount)}</Glossary>
              </Text>
              {availablePrograms && availablePrograms.length > 0 && (
                <FormControl>
                  <FormLabel>{programGroupName}</FormLabel>
                  <Select
                    name="program"
                    id="program"
                    isRequired={true}
                    placeholder="Select an option"
                    onChange={(event) => {
                      const target = event.target;
                      setProgramSelected(target.value);
                      setClosedCapacityWarning(false);
                    }}
                    value={programSelected ?? ""}
                  >
                    {availablePrograms.map((data) => (
                      <option key={data.id} value={data.id}>
                        {data.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!closedOfferExpiredInfo && offerExpirationInfo.length > 0 && (
                <Alert status="info" variant="subtle" borderRadius={6}>
                  <AlertIcon alignSelf="flex-start" />
                  <AlertDescription width="100%" flex="1">
                    {renderExpiredOfferInfoBody()}
                  </AlertDescription>
                  <CloseButton
                    alignSelf="flex-start"
                    position="relative"
                    right={-1}
                    top={-1}
                    onClick={() => {
                      setClosedOfferExpiredInfo(true);
                    }}
                  />
                </Alert>
              )}
              {offerExpirationRD.isLoading() && (
                <CircularProgress isIndeterminate color="primary.500" />
              )}
              {!closedCapacityWarning &&
                schoolGradesOverCapacity &&
                schoolGradesOverCapacity.length > 0 && (
                  <Alert status="warning" variant="subtle" borderRadius={6}>
                    <AlertIcon alignSelf="flex-start" />
                    <AlertDescription>
                      {renderSchoolGradesOverCapacity()}
                    </AlertDescription>
                    <CloseButton
                      alignSelf="flex-start"
                      position="relative"
                      right={-1}
                      top={-1}
                      onClick={() => {
                        setClosedCapacityWarning(true);
                      }}
                    />
                  </Alert>
                )}
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter borderTop="1px solid" borderColor="gray.300">
            <Button
              type="button"
              ref={cancelRef}
              colorScheme="gray"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              colorScheme="blue"
              ml={3}
              isDisabled={isMissingProgram || offerExpirationRD.isLoading()}
            >
              {confirmButtonLabel(offerCount)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );

  return { confirm, confirmationDialog };
};
